// SPDX-License-Identifier: AGPL-3.0-only

import {
  validateDemGrid,
  type DemGrid,
  type GeographicBounds
} from '@openfloodlab/simulation-core';
import { sha256 } from '@noble/hashes/sha2.js';

export interface DemDatasetManifest {
  readonly schemaVersion: 1;
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly width: number;
  readonly height: number;
  readonly bounds: GeographicBounds;
  readonly encoding: 'float32-le';
  readonly dataFile: string;
  readonly minimumElevationMeters: number;
  readonly maximumElevationMeters: number;
  readonly sha256: string;
  readonly source: {
    readonly provider: string;
    readonly dataset: string;
    readonly license: string;
    readonly accessed: string;
  };
  readonly suggestedView: {
    readonly center: readonly [number, number];
    readonly zoom: number;
    readonly pitch: number;
    readonly bearing: number;
  };
  readonly suggestedSource: {
    readonly longitude: number;
    readonly latitude: number;
    readonly radiusMeters: number;
    readonly flowRateCubicMetersPerSecond: number;
  };
}

export interface LoadedDemDataset {
  readonly manifest: DemDatasetManifest;
  readonly grid: DemGrid;
}

export interface LoadDemDatasetOptions {
  readonly signal?: AbortSignal;
  readonly targetResolution?: number;
}

export async function loadDemDataset(
  manifestUrl: string,
  options: LoadDemDatasetOptions = {}
): Promise<LoadedDemDataset> {
  const requestOptions = options.signal ? { signal: options.signal } : undefined;
  const manifestResponse = await fetch(manifestUrl, requestOptions);
  if (!manifestResponse.ok) {
    throw new Error(`Could not load DEM manifest (${manifestResponse.status}).`);
  }
  const manifest = (await manifestResponse.json()) as DemDatasetManifest;
  if (manifest.schemaVersion !== 1 || manifest.encoding !== 'float32-le') {
    throw new Error('Unsupported DEM manifest format.');
  }
  if (!/^[a-f0-9]{64}$/i.test(manifest.sha256)) {
    throw new Error('DEM manifest does not contain a valid SHA-256 checksum.');
  }
  const dataUrl = new URL(manifest.dataFile, manifestResponse.url || manifestUrl);
  const dataResponse = await fetch(dataUrl, requestOptions);
  if (!dataResponse.ok) throw new Error(`Could not load DEM values (${dataResponse.status}).`);
  const buffer = await dataResponse.arrayBuffer();
  const expectedBytes = manifest.width * manifest.height * 4;
  if (buffer.byteLength !== expectedBytes) {
    throw new Error(`DEM byte length is ${buffer.byteLength}; expected ${expectedBytes}.`);
  }
  const checksum = await sha256Hex(buffer);
  if (checksum !== manifest.sha256.toLowerCase()) {
    throw new Error('DEM SHA-256 checksum does not match its manifest.');
  }
  const sourceElevations = decodeFloat32LittleEndian(buffer);
  const targetResolution = Math.max(
    3,
    Math.round(options.targetResolution ?? Math.max(manifest.width, manifest.height))
  );
  const elevations = resampleElevations(
    sourceElevations,
    manifest.width,
    manifest.height,
    targetResolution,
    targetResolution
  );
  const grid: DemGrid = {
    id: manifest.id,
    width: targetResolution,
    height: targetResolution,
    bounds: manifest.bounds,
    elevations
  };
  validateDemGrid(grid);
  return {
    manifest,
    grid
  };
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  const digest = subtle
    ? new Uint8Array(await subtle.digest('SHA-256', buffer))
    : sha256(new Uint8Array(buffer));
  return Array.from(digest, (value) => value.toString(16).padStart(2, '0')).join('');
}

function decodeFloat32LittleEndian(buffer: ArrayBuffer): Float32Array {
  const view = new DataView(buffer);
  const values = new Float32Array(buffer.byteLength / 4);
  for (let index = 0; index < values.length; index += 1) {
    values[index] = view.getFloat32(index * 4, true);
  }
  return values;
}

function resampleElevations(
  source: Float32Array,
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number
): Float32Array {
  if (sourceWidth === targetWidth && sourceHeight === targetHeight) return source;
  const result = new Float32Array(targetWidth * targetHeight);
  for (let y = 0; y < targetHeight; y += 1) {
    const sourceY = (y / (targetHeight - 1)) * (sourceHeight - 1);
    const y0 = Math.floor(sourceY);
    const y1 = Math.min(sourceHeight - 1, y0 + 1);
    const fy = sourceY - y0;
    for (let x = 0; x < targetWidth; x += 1) {
      const sourceX = (x / (targetWidth - 1)) * (sourceWidth - 1);
      const x0 = Math.floor(sourceX);
      const x1 = Math.min(sourceWidth - 1, x0 + 1);
      const fx = sourceX - x0;
      const top =
        (source[y0 * sourceWidth + x0] ?? 0) * (1 - fx) + (source[y0 * sourceWidth + x1] ?? 0) * fx;
      const bottom =
        (source[y1 * sourceWidth + x0] ?? 0) * (1 - fx) + (source[y1 * sourceWidth + x1] ?? 0) * fx;
      result[y * targetWidth + x] = top * (1 - fy) + bottom * fy;
    }
  }
  return result;
}
